import { Box, Flex, HStack, Icon, Text, VStack } from '@chakra-ui/react'
import { Separator as Divider } from '@chakra-ui/react'
import { ChevronDown, ChevronUp, User } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMainWindow } from '~/hooks/useElectronWindow'
import { useLocalStorageObjectKey } from '~/hooks/useLocalStorage'
import { useSharedVariable } from '~/hooks/useSharedVariable'
import { MessageButtons } from '~/pages/Popup/MessageButtons'
import { useMarkMessageThreadAsRead } from '~/queries/messagingQueries'
import { phoneFormatter } from '~/utils/formatter'
import {
  removeItemFromQueueById,
  removeMessagesFromQueueByPhoneNumber,
} from '~/utils/popupQueueUtils'

import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'
import { Toggle } from '@mangovoice/mango-ui'
import { MessageQueueItem, PopupQueueItem } from '@mangovoice/types'

interface MessageListProps {
  messageItems: MessageQueueItem[]
}

const getTimeAgo = (
  dateString: string,
  t: (key: string, options?: any) => string,
) => {
  const now = new Date()
  const messageDate = new Date(dateString)
  const diffInMinutes = Math.floor(
    (now.getTime() - messageDate.getTime()) / (1000 * 60),
  )

  if (diffInMinutes < 1) {
    return t('global.timeAgo.justNow')
  } else if (diffInMinutes < 60) {
    return t('global.timeAgo.minutesAgo', { count: diffInMinutes })
  } else if (diffInMinutes < 1440) {
    const hours = Math.floor(diffInMinutes / 60)
    return t('global.timeAgo.hoursAgo', { count: hours })
  } else {
    const days = Math.floor(diffInMinutes / 1440)
    return t('global.timeAgo.daysAgo', { count: days })
  }
}

interface MessageItemHeaderProps {
  messageItem: MessageQueueItem
  isExpanded: boolean
  onToggle?: () => void
}

const MessageItemHeader: React.FC<MessageItemHeaderProps> = ({
  messageItem,
  isExpanded,
  onToggle,
}) => {
  const { t } = useTranslation()
  const displayName = messageItem?.name || 'Unknown Sender'
  const displayNumber = phoneFormatter(messageItem?.external_phone_number || '')
  const latestMessage = messageItem?.latest_message

  const timeAgo = latestMessage?.created_at
    ? getTimeAgo(latestMessage.created_at, t)
    : ''

  return (
    <HStack gap={3} width="100%" align="flex-start">
      <Flex
        align="center"
        justify="center"
        w={10}
        h={10}
        borderRadius="full"
        bg={isExpanded ? 'bg.muted' : 'bg.subtle'}
        color="fg.subtle"
        flexShrink={0}
      >
        <User width={20} height={20} />
      </Flex>
      <VStack align="flex-start" flex={1} minWidth={0} gap={1}>
        <HStack justify="space-between" width="100%">
          <Text {...textStyles[textStyleKeys.Body2_Bold]} lineClamp={1}>
            {displayName}
          </Text>
          <HStack gap={2} flexShrink={0} color="fg.muted">
            {timeAgo && (
              <Text {...textStyles[textStyleKeys.Body3_Bold]}>{timeAgo}</Text>
            )}
            {onToggle && (
              <Icon
                as={isExpanded ? ChevronUp : ChevronDown}
                boxSize={5}
                cursor="pointer"
                color="currentColor"
                onClick={onToggle}
              />
            )}
          </HStack>
        </HStack>
        <Text
          {...textStyles[textStyleKeys.Body3]}
          lineClamp={1}
          width="100%"
          textAlign="left"
          color="fg.subtle"
        >
          {displayNumber}
        </Text>
      </VStack>
    </HStack>
  )
}

const MessageItemExpanded: React.FC<{
  messageItem: MessageQueueItem
  onCollapse?: () => void
  isPrivacyActive: boolean
}> = ({ messageItem, onCollapse, isPrivacyActive }) => {
  const { focusMainWindow } = useMainWindow()
  const [messageQueue, setMessageQueue] =
    useSharedVariable<PopupQueueItem[]>('MessageQueue')
  const { mutate: markThreadAsRead } = useMarkMessageThreadAsRead()

  const latestMessage = messageItem?.latest_message

  const handleOpenMango = () => {
    // Mark the thread as read
    if (messageItem.id && (messageItem.unread_message_count ?? 0) > 0) {
      markThreadAsRead(messageItem.id)
    }
    // Dismiss all messages from this phone number/thread
    handleDismissAllFromThread()
    // Pass the thread ID to open the specific message thread
    const threadId = messageItem.id
    focusMainWindow('open-messages', { threadId })
  }

  const handleDismiss = () => {
    // Use latest_message.id to match how items are added to the queue
    const messageId = String(messageItem.latest_message?.id || messageItem.id)
    setMessageQueue(removeItemFromQueueById(messageQueue || [], messageId))
  }

  const handleDismissAllFromThread = () => {
    // Remove all messages from the same phone number
    if (messageItem.external_phone_number) {
      setMessageQueue(
        removeMessagesFromQueueByPhoneNumber(
          messageQueue || [],
          messageItem.external_phone_number,
        ),
      )
    } else {
      // Fallback to just dismissing this message if no phone number
      handleDismiss()
    }
  }

  return (
    <Box
      border="1px solid"
      borderColor="brand.solid"
      borderRadius="lg"
      bg="brand.subtle"
      width="100%"
    >
      <Box p={4} pb={2}>
        <MessageItemHeader
          messageItem={messageItem}
          isExpanded={true}
          onToggle={onCollapse}
        />
      </Box>

      <Box px={4}>
        <Divider borderColor="brand.muted" my={2} />
      </Box>

      <Box p={4} py={0}>
        <Box
          style={{
            filter: isPrivacyActive ? 'blur(3px)' : 'none',
          }}
        >
          {latestMessage && (
            <Text {...textStyles[textStyleKeys.Body3]} color="fg.muted">
              {latestMessage.body}
            </Text>
          )}
        </Box>
      </Box>

      <Box p={4} pt={2}>
        <MessageButtons
          handleOpenMango={handleOpenMango}
          handleDismiss={handleDismiss}
        />
      </Box>
    </Box>
  )
}

export const MessageList: React.FC<MessageListProps> = React.memo(
  ({ messageItems }) => {
    const [expandedMessageId, setExpandedMessageId] = useState<string | null>(
      messageItems.length > 0 ? messageItems[0]._id : null,
    )

    const [isPrivacyActive, onTogglePrivacy] =
      useLocalStorageObjectKey<boolean>({
        storageKey: 'popupSettings',
        keyInObject: 'textPrivacy',
        initialValue: true,
      })

    const handleToggle = (messageId: string) => {
      setExpandedMessageId(expandedMessageId === messageId ? null : messageId)
    }

    const hasMultipleMessages = messageItems.length > 1

    // Auto-expand when only one message remains
    useEffect(() => {
      if (
        messageItems.length === 1 &&
        expandedMessageId !== messageItems[0]._id
      ) {
        setExpandedMessageId(messageItems[0]._id)
      }
    }, [messageItems, expandedMessageId])

    return (
      <VStack gap={3} align="end">
        <Flex justifyContent={'flex-end'} width={'100%'}>
          <Toggle
            id="message-privacy-toggle"
            checked={isPrivacyActive}
            onCheckedChange={(details: { checked: boolean }) =>
              onTogglePrivacy(details.checked)
            }
            flexDirection="row-reverse"
          >
            <Text
              lineClamp={1}
              {...textStyles[textStyleKeys.Body3]}
              color="inherit"
            >
              Privacy
            </Text>
          </Toggle>
        </Flex>

        {messageItems.map((messageItem) => {
          const isExpanded = expandedMessageId === messageItem._id

          return (
            <Box key={messageItem._id} width="100%">
              {isExpanded ? (
                <MessageItemExpanded
                  messageItem={messageItem}
                  onCollapse={
                    hasMultipleMessages
                      ? () => handleToggle(messageItem._id)
                      : undefined
                  }
                  isPrivacyActive={isPrivacyActive}
                />
              ) : (
                <Flex
                  as="button"
                  onClick={() => handleToggle(messageItem._id)}
                  width="100%"
                  p={4}
                  border="1px solid"
                  borderColor="border.muted"
                  borderRadius="lg"
                  bg="bg.panel"
                  cursor="pointer"
                  _hover={{
                    bg: 'bg.subtle',
                  }}
                  transition="background-color 0.2s"
                >
                  <MessageItemHeader
                    messageItem={messageItem}
                    isExpanded={isExpanded}
                  />
                </Flex>
              )}
            </Box>
          )
        })}
      </VStack>
    )
  },
  (prevProps, nextProps) => {
    // TODO: Validate if this is needed
    // Custom comparison to prevent re-renders when message data is the same
    if (prevProps.messageItems.length !== nextProps.messageItems.length) {
      return false
    }

    // Compare each message item
    for (let i = 0; i < prevProps.messageItems.length; i++) {
      const prevMessage = prevProps.messageItems[i]
      const nextMessage = nextProps.messageItems[i]

      if (
        prevMessage?.id !== nextMessage?.id ||
        prevMessage?.name !== nextMessage?.name ||
        prevMessage?.external_phone_number !==
          nextMessage?.external_phone_number ||
        prevMessage?.unread_message_count !==
          nextMessage?.unread_message_count ||
        prevMessage?.latest_message?.id !== nextMessage?.latest_message?.id ||
        prevMessage?.latest_message?.body !== nextMessage?.latest_message?.body
      ) {
        return false
      }
    }

    return true
  },
)
